/* Object and hand highlights — the pure part: which boxes belong to which
 * sub-task, and where a box is at a given moment. The drawing is in
 * components/HighlightOverlay.tsx.
 *
 * STALE BOXES ARE HIDDEN, NOT RELABELLED. A segment of the highlights file is
 * shown for a sub-task only while the sub-task still has the start, end, object,
 * tool and target the boxes were computed for. Once the crowd renames the object
 * or moves a boundary, the boxes are for something else, so they disappear and
 * the overlay says why ("edited after detection"). */

import type { HighlightSegment, HighlightTrack, Highlights, Subtask } from "../api/types";

export const TIME_TOL = 0.05;     // the tool's own boundary tolerance (6.3.1)
export const MAX_GAP = 1.0;       // s: interpolate across detector gaps up to this long
export const EDGE = 0.25;         // s: hold the first/last box this long past its point
export const MAX_MOVE = 0.15;     // a box centre moving more than 0.15 of the frame between two saved points
export const MAX_SPEED = 1.5;     // ... faster than 1.5 frames per second is a re-anchor onto something else,
                                  // not motion: snap to the nearer point instead of sliding (no streak, no trail)

/* A blank name, whatever its spelling: the ingest keeps LLM-1's text as given (a literal "null" stays
   "null"), while an annotator who clears a field leaves "" or null. All of them mean "no name". */
const BLANK = new Set(["", "null", "none", "-"]);
const norm = (v: string | null | undefined) => {
  const s = (v ?? "").trim().toLowerCase();
  return BLANK.has(s) ? "" : s;
};

export type Match =
  | { kind: "match"; segment: HighlightSegment }
  | { kind: "stale" }              // same span, but the names were edited since
  | { kind: "none" };              // no boxes were computed for this span

type SubFields = Pick<Subtask, "start_seconds" | "end_seconds" | "object_name" | "tool" | "target">;

export function segmentFor(h: Highlights | null | undefined, sub: SubFields | null | undefined): Match {
  if (!h?.available || !h.segments || !sub) return { kind: "none" };
  let sameSpan = false;
  for (const seg of h.segments) {
    const span = Math.abs(seg.start - sub.start_seconds) <= TIME_TOL
              && Math.abs(seg.end - sub.end_seconds) <= TIME_TOL;
    if (!span) continue;
    sameSpan = true;
    if (norm(seg.object_name) === norm(sub.object_name) && norm(seg.tool) === norm(sub.tool)
        && norm(seg.target) === norm(sub.target)) {
      return { kind: "match", segment: seg };
    }
  }
  return sameSpan ? { kind: "stale" } : { kind: "none" };
}

export type Box = [number, number, number, number];

/** The box of a track at time t, linearly interpolated; null when the track has
 *  no detection near t (a gap longer than MAX_GAP, or outside the track). */
export function boxAt(track: HighlightTrack, t: number): Box | null {
  const p = track.points;
  if (!p.length) return null;
  if (t < p[0][0] - EDGE || t > p[p.length - 1][0] + EDGE) return null;
  if (t <= p[0][0]) return [p[0][1], p[0][2], p[0][3], p[0][4]];
  if (t >= p[p.length - 1][0]) { const q = p[p.length - 1]; return [q[1], q[2], q[3], q[4]]; }
  // binary search for the pair around t
  let lo = 0, hi = p.length - 1;
  while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (p[mid][0] <= t) lo = mid; else hi = mid; }
  const a = p[lo], b = p[hi];
  if (b[0] - a[0] > MAX_GAP) return null;
  const move = Math.hypot((b[1] + b[3] - a[1] - a[3]) / 2, (b[2] + b[4] - a[2] - a[4]) / 2);
  if (move > MAX_MOVE && move / Math.max(1e-6, b[0] - a[0]) > MAX_SPEED) return t - a[0] < b[0] - t ? [a[1], a[2], a[3], a[4]] : [b[1], b[2], b[3], b[4]];
  const w = (t - a[0]) / Math.max(1e-6, b[0] - a[0]);
  return [a[1] + (b[1] - a[1]) * w, a[2] + (b[2] - a[2]) * w,
          a[3] + (b[3] - a[3]) * w, a[4] + (b[4] - a[4]) * w];
}

/** Centre points of the box over the last `seconds` (half a second: in first-person video a longer trail
 *  mostly draws the wearer's head turning), newest first, stopping at
 *  a gap or a jump (a trail is never drawn across a different object). */
export function trailAt(track: HighlightTrack, t: number, seconds = 0.5, steps = 10,
                        maxJump = 0.12): [number, number][] {
  const out: [number, number][] = [];
  for (let k = 0; k <= steps; k++) {
    const b = boxAt(track, t - (seconds * k) / steps);
    if (!b) break;
    const c: [number, number] = [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
    if (out.length) {
      const [px, py] = out[out.length - 1];
      if (Math.hypot(c[0] - px, c[1] - py) > maxJump) break;
    }
    out.push(c);
  }
  return out;
}

/** Where the picture actually is inside an element that letterboxes it
 *  (object-fit: contain): [left, top, width, height] in CSS pixels. */
export function containRect(boxW: number, boxH: number, vidW: number, vidH: number): Box {
  if (!vidW || !vidH || !boxW || !boxH) return [0, 0, boxW, boxH];
  const s = Math.min(boxW / vidW, boxH / vidH);
  const w = vidW * s, h = vidH * s;
  return [(boxW - w) / 2, (boxH - h) / 2, w, h];
}

export const ROLE_COLOR: Record<HighlightTrack["role"], string> = {
  hand: "#3ddc84", object: "#ff9f1a", tool: "#3b9dff", target: "#ff4d6d",
};
